
import { useEffect, useRef, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "../styles/BlogPost.css";

// ── Video embed ─────────────────────────────────────────────────────────

function VideoEmbed({ src, title }) {
  const videoRef = useRef(null);
  const [isFloating, setIsFloating] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!videoRef.current) return;

      const rect = videoRef.current.getBoundingClientRect();

      // Once the video has mostly scrolled off screen,
      // turn it into a small floating player.
      if (rect.bottom < 80 && isPlaying) {
        setIsFloating(true);
      } else if (rect.top > window.innerHeight - 100) {
        setIsFloating(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isPlaying]);

  return (
    <>
      {/* Main video location inside the article */}
      <div
        ref={videoRef}
        className={`blog-video-container ${
          isFloating ? "video-placeholder" : ""
        }`}
      >
        <div className="blog-video-card">
          <div className="blog-video-header">
            <div>
              <span className="blog-video-label">FOCUS ON FOCUS</span>
              <h2>{title}</h2>
            </div>

            <span className="blog-video-duration">
              Podcast Episode 1
            </span>
          </div>

          <div className="blog-video-wrap">
            <iframe
              className="blog-video-frame"
              src={`${src}&enablejsapi=1`}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              onLoad={() => setIsPlaying(true)}
            />
          </div>

          <div className="blog-video-caption">
            <span>▶</span>
            Watch Episode 1: Tennis, focus, pressure, and life.
          </div>
        </div>
      </div>

      {/* Floating video */}
      {isFloating && (
        <div className="floating-video">
          <div className="floating-video-header">
            <span>Focus on Focus</span>

            <button
              className="floating-video-close"
              onClick={() => setIsFloating(false)}
              aria-label="Close floating video"
            >
              ×
            </button>
          </div>

          <iframe
            src={`${src}&enablejsapi=1`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      )}
    </>
  );
}

// ── Blog post ───────────────────────────────────────────────────────────

function BlogPostFocusOnFocus() {
  return (
    <>
      <Navbar />

      <article className="blog-post">

        {/* ── Breadcrumb ── */}
        <p className="blog-breadcrumb">
          <span>Podcast & Tennis</span>
          <span className="breadcrumb-divider">|</span>
          <span className="breadcrumb-section">
            Tennis | Mindset | Family
          </span>
        </p>

        {/* ── Headline & dek ── */}
        <h1 className="blog-headline">
          Starting "Focus on Focus": A Podcast With My Dad About Tennis
          and Life
        </h1>

        <p className="blog-dek">
          A racket sport turns out to be a pretty good teacher. My dad
          and I started a podcast to talk about what tennis has taught us
          about attention, pressure, and showing up — on the court and
          off it.
        </p>

        {/* ── Share row ── */}
        <div className="blog-meta-row">
          <button
            className="blog-share-btn"
            onClick={() =>
              navigator.share?.({
                title: document.title,
                url: window.location.href,
              })
            }
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            Share
          </button>
        </div>

        {/* ── Date + read time ── */}
        <p className="blog-dateline">
          New episode weekly &nbsp;|&nbsp; 3 min read
        </p>

        {/* ── Embedded video ── */}
        <VideoEmbed
          src="https://www.youtube.com/embed/eVhtZSFstKE?start=454"
          title="Focus on Focus: A Podcast About Tennis and Life"
        />

        {/* ── Body copy ── */}
        <div className="blog-body">

          <p className="blog-drop-cap">
            My dad and I have been talking about tennis at the dinner
            table, during car rides, on walks, with other people, for as long as I can remember. Somewhere along the
            way we realized the conversation was not only about
            tennis but also about focus, patience, and how you respond
            when things aren't going your way. So we started recording
            it. The result is "Focus on Focus: A Podcast About Tennis
            and Life." Episode 1 just dropped, and you can watch it in
            the video above.
          </p>

          <p>
            Tennis is a strange sport to play well. You're alone on the
            court, no teammates to lean on, no coach calling plays
            between points. Every mistake is visible, and every point
            ends before you've had much time to think. What separates
            players who compete well from players who just hit the ball
            hard has almost nothing to do with strokes and almost
            everything to do with what's happening between the ears —
            how you handle a blown lead, a bad line call, a hot
            afternoon when your legs feel heavy in the third set.
          </p>

          <p>
            That's the thread we pull on in every episode. We'll talk
            about a specific match, a specific habit, or a specific
            mental trap. For example, the tendency to play the score
            instead of the point in front of you, the difference between
            confidence and overconfidence, why the best competitors seem
            to have short memories (is this true?), and then we'll ask
            the obvious next question: where else does this show up?
            A preview of that answer: In a work deadline. In a hard
            conversation. In a bad week that you have to keep showing
            up for anyway.
          </p>

          <p>
            My dad has been playing and coaching tennis for decades, and
            I grew up on the court trying to keep up with him. Making
            this podcast together has turned out to be its own kind of
            practice for both of us, and for anyone listening who's
            trying to figure out how to stay locked in when it counts.
          </p>

        </div>

        <div className="blog-body">

          <p className="blog-takeaway">
            <strong>Takeaway:</strong> Focus isn't something you either
            have or don't. Instead, it's a skill you train, one point
            (or one day) at a time. That's what "Focus on Focus" is
            about. It equips you with the skills to focus better, both
            on and off court. New episodes drop weekly; give the first
            one a watch above and leave a comment to let us know what
            resonates.
          </p>

        </div>

      </article>

      <Footer />
    </>
  );
}

export default BlogPostFocusOnFocus;